// Mortgage and affordability calculations

export function calculateMonthlyPayment(principal, annualRate, years = 30) {
  const monthlyRate = annualRate / 100 / 12;
  const numPayments = years * 12;
  
  if (monthlyRate === 0) {
    return principal / numPayments;
  }
  
  const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                  (Math.pow(1 + monthlyRate, numPayments) - 1);
  return payment;
}

export function calculateTotalCosts(propertyData, userInputs) {
  const { price, propertyTax, hoaFee = 0, estimatedInsurance } = propertyData;
  const { downPayment, loanType, interestRate, customDownPercent } = userInputs;

  let downPaymentPercent;
  let downPaymentAmount;
  
  // Determine down payment percentage based on loan type
  if (loanType === 'fha') {
    downPaymentPercent = 3.5;
  } else if (loanType === 'conventional') {
    downPaymentPercent = 3;
  } else {
    downPaymentPercent = customDownPercent;
  }

  // Use user-entered dollar amount if provided, otherwise calculate from percent
  downPaymentAmount = downPayment > 0 ? downPayment : (price * downPaymentPercent / 100);
  const loanAmount = price - downPaymentAmount;
  const actualDownPercent = (downPaymentAmount / price) * 100;

  // Calculate monthly payments
  const principalInterest = calculateMonthlyPayment(loanAmount, interestRate);
  const monthlyTax = propertyTax / 12;
  const monthlyInsurance = estimatedInsurance / 12;
  const monthlyHOA = hoaFee / 12;
  
  // PMI calculation (if less than 20% down)
  let monthlyPMI = 0;
  if (actualDownPercent < 20) {
    if (loanType === 'fha') {
      // FHA has both upfront and monthly MIP
      monthlyPMI = loanAmount * 0.0085 / 12; // 0.85% annually for FHA
    } else {
      // Conventional PMI typically 0.5-1% of loan annually
      monthlyPMI = loanAmount * 0.007 / 12; // 0.7% annually
    }
  }

  const totalMonthly = principalInterest + monthlyTax + monthlyInsurance + monthlyHOA + monthlyPMI;

  // Closing costs estimate (typically 2-5% of purchase price)
  const estimatedClosingCosts = price * 0.03;
  const totalCashNeeded = downPaymentAmount + estimatedClosingCosts;

  // Income needed (using 28% front-end ratio)
  const monthlyIncomeNeeded = totalMonthly / 0.28;
  const annualIncomeNeeded = monthlyIncomeNeeded * 12;

  return {
    downPaymentAmount,
    downPaymentPercent: actualDownPercent,
    loanAmount,
    principalInterest,
    monthlyTax,
    monthlyInsurance,
    monthlyHOA,
    monthlyPMI,
    totalMonthly,
    estimatedClosingCosts,
    totalCashNeeded,
    annualIncomeNeeded,
    monthlyIncomeNeeded
  };
}